/* Created by Interactive 63 - https://interactive63.com */
/**
 * The morph editor: the previous shape and this one side by side with their points numbered, so
 * points can be pinned to the points they become, plus a choice of which part grows from which
 * for multi-part shapes and a scrubber previewing the result. Changes apply as one undo step.
 */
import { buildMorphTrack } from '../../core/morph/build';
import { formatPath, lerpValues } from '../../core/morph/format';
import { largestFirst } from '../../core/morph/match';
import { toCubicSubpaths, type Subpath } from '../../core/morph/normalise';
import { anchorPoints, preparePinnedFrames, type Pin } from '../../core/morph/pins';
import { isMorphValue, type MorphValue } from '../../core/model/types';
import { findKeyframe } from '../../core/timeline/keyframes';
import { setKeyframeValue } from '../store/actions';
import type { Store } from '../store/store';
import { h } from '../ui/dom';
import { toast } from '../ui/toast';

const SVG_NS = 'http://www.w3.org/2000/svg';
/** Colours that tell pins apart; they repeat after the last. */
const PIN_COLOURS = ['#e8590c', '#1c7ed6', '#2f9e44', '#ae3ec9', '#f08c00', '#0c8599', '#c2255c'];
/** Size of each shape pane, in CSS pixels. */
const PANE_SIZE = 180;
/** Space around a shape inside its pane, as a share of its size. */
const PANE_MARGIN = 0.12;
/** Radius of a point marker, as a share of the shape's size. */
const POINT_RADIUS = 0.025;

interface Anchor {
  index: number;
  part: number;
  x: number;
  y: number;
}

/** A shape's parts in the order pins count them, and every point with its overall index. */
function readShape(d: string): { parts: Subpath[]; anchors: Anchor[] } | null {
  const parsed = toCubicSubpaths(d);
  if (!parsed.ok) return null;
  const parts = largestFirst(parsed.subpaths);
  const anchors: Anchor[] = [];
  parts.forEach((path, part) => {
    for (const [x, y] of anchorPoints(path)) anchors.push({ index: anchors.length, part, x, y });
  });
  return { parts, anchors };
}

/** Creates an SVG element with attributes. */
function svg<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [name, value] of Object.entries(attrs)) el.setAttribute(name, String(value));
  return el;
}

/** The view box that frames a set of points with a margin. */
function frameFor(points: readonly { x: number; y: number }[]): {
  box: string;
  size: number;
} {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const x0 = Math.min(...xs);
  const y0 = Math.min(...ys);
  const size = Math.max(Math.max(...xs) - x0, Math.max(...ys) - y0, 1);
  const margin = size * PANE_MARGIN;
  return { box: `${x0 - margin} ${y0 - margin} ${size + margin * 2} ${size + margin * 2}`, size };
}

/**
 * Opens the morph editor for a morph keyframe that has a shape before it. Resolves when the
 * dialog closes.
 */
export function openMorphEditor(store: Store, keyframeId: string): Promise<void> {
  const found = findKeyframe(store.getState().project, keyframeId);
  const before = found ? found.track.keyframes[found.index - 1] : undefined;
  const value = found?.keyframe.value;
  if (!found || !before || !isMorphValue(value) || !isMorphValue(before.value)) {
    toast.info('Choose a shape keyframe that has a shape before it.');
    return Promise.resolve();
  }
  const from = readShape(before.value.d);
  const to = readShape(value.d);
  if (!from || !to) {
    toast.info("One of the shapes couldn't be read.");
    return Promise.resolve();
  }
  const fromD = before.value.d;
  const current: MorphValue = value;

  let pins: Pin[] = [...(current.pins ?? [])];
  let parts: (number | null)[] = to.parts.map((_, i) => current.parts?.[i] ?? i);
  let pending: number | null = null;
  let progress = 0.5;

  return new Promise((resolve) => {
    const dialog = h('dialog', {
      class: 'settings-dialog morph-editor',
      'aria-label': 'Edit morph',
    });
    const leftPane = h('div', { class: 'morph-pane' });
    const rightPane = h('div', { class: 'morph-pane' });
    const previewPane = h('div', { class: 'morph-pane' });
    const pinList = h('ul', { class: 'morph-pins' });
    const message = h('p', { class: 'settings-note', role: 'status' });
    const partsBox = h('div', { class: 'morph-parts' });
    const scrub = h('input', {
      type: 'range',
      min: 0,
      max: 100,
      value: 50,
      'aria-label': 'Preview the morph',
    });

    /** The value the keyframe would have with the current pins and parts. */
    const draftValue = (): MorphValue => {
      const keepParts = to.parts.length > 1 || from.parts.length > 1;
      return {
        d: current.d,
        ...(current.align ? { align: current.align } : {}),
        ...(current.placement ? { placement: current.placement } : {}),
        ...(current.sourceId ? { sourceId: current.sourceId } : {}),
        ...(pins.length > 0 ? { pins: pins.map(([a, b]) => [a, b]) } : {}),
        ...(keepParts ? { parts: [...parts] } : {}),
      };
    };

    /** Draws one shape with its numbered points; clicking a point calls `onPick`. */
    const drawShape = (
      pane: HTMLElement,
      d: string,
      anchors: readonly Anchor[],
      side: 0 | 1,
      title: string,
    ): void => {
      const { box, size } = frameFor(anchors);
      const root = svg('svg', { viewBox: box, width: PANE_SIZE, height: PANE_SIZE, role: 'img' });
      root.append(svg('path', { d, class: 'morph-shape' }));
      const r = size * POINT_RADIUS;
      for (const anchor of anchors) {
        const pinIndex = pins.findIndex((p) => p[side] === anchor.index);
        const colour = pinIndex >= 0 ? PIN_COLOURS[pinIndex % PIN_COLOURS.length] : undefined;
        const isPending = side === 0 && pending === anchor.index;
        const dot = svg('circle', {
          cx: anchor.x,
          cy: anchor.y,
          r: pinIndex >= 0 || isPending ? r * 1.6 : r,
          class: ['morph-point', isPending ? 'is-pending' : ''].join(' ').trim(),
          ...(colour ? { style: `fill: ${colour}` } : {}),
        });
        const label = svg('text', {
          x: anchor.x + r * 1.8,
          y: anchor.y - r * 1.8,
          'font-size': r * 3.2,
          class: 'morph-point-label',
        });
        label.textContent = String(anchor.index + 1);
        const pick = (): void => {
          if (side === 0) {
            pending = pending === anchor.index ? null : anchor.index;
          } else if (pending !== null) {
            const fromIndex = pending;
            pins = [
              ...pins.filter((p) => p[0] !== fromIndex && p[1] !== anchor.index),
              [fromIndex, anchor.index],
            ];
            pending = null;
          } else {
            message.textContent = 'Click a point on the left first, then the point it becomes.';
            return;
          }
          render();
        };
        dot.addEventListener('click', pick);
        label.addEventListener('click', pick);
        root.append(dot, label);
      }
      pane.replaceChildren(h('p', { class: 'morph-pane-title' }, title), root);
    };

    /** Redraws the preview at the scrubber's position from the morph as it would be built. */
    const drawPreview = (): void => {
      const built = buildMorphTrack({
        elementId: 'preview',
        frames: [
          { time: 0, d: fromD, easing: { type: 'linear' } },
          {
            time: 1000,
            d: current.d,
            easing: { type: 'linear' },
            ...(current.align ? { align: current.align } : {}),
            ...(pins.length > 0 ? { pins } : {}),
            ...(draftValue().parts ? { parts } : {}),
          },
        ],
        evenOdd: false,
        decimals: 3,
      });
      if (!built.ok) {
        previewPane.replaceChildren(h('p', { class: 'settings-note' }, built.message));
        return;
      }
      const [a, b] = built.data.frames;
      const values = lerpValues(a?.values ?? [], b?.values ?? [], progress, []);
      const d = formatPath(built.data.template, values);
      const all = [...from.anchors, ...to.anchors];
      const root = svg('svg', {
        viewBox: frameFor(all).box,
        width: PANE_SIZE,
        height: PANE_SIZE,
        role: 'img',
      });
      root.append(svg('path', { d, class: 'morph-shape' }));
      previewPane.replaceChildren(h('p', { class: 'morph-pane-title' }, 'Preview'), root, scrub);
    };

    /** Lists the pins with a remove button each, and explains any problem with them. */
    const drawPins = (): void => {
      pinList.replaceChildren(
        ...pins.map((pin, i) => {
          const remove = h(
            'button',
            { type: 'button', 'aria-label': `Remove pin ${i + 1}` },
            'Remove',
          );
          remove.addEventListener('click', () => {
            pins = pins.filter((p) => p !== pin);
            render();
          });
          return h(
            'li',
            null,
            h('span', {
              class: 'morph-pin-swatch',
              style: { background: PIN_COLOURS[i % PIN_COLOURS.length] ?? '' },
            }),
            `Point ${pin[0] + 1} becomes point ${pin[1] + 1} `,
            remove,
          );
        }),
      );
      const check = preparePinnedFrames(
        [from.parts, to.parts],
        { allowReverse: false },
        [],
        [undefined, draftValue()],
      );
      if (pending !== null)
        message.textContent = `Now click the point that point ${pending + 1} becomes.`;
      else if (check.problems[0]) message.textContent = check.problems[0];
      else if (pins.length === 0)
        message.textContent = 'Click a point on the left, then the point it becomes on the right.';
      else message.textContent = 'Pinned points meet exactly; the rest are matched automatically.';
    };

    /** Offers a choice of partner for each part when either shape has several. */
    const drawParts = (): void => {
      if (to.parts.length < 2 && from.parts.length < 2) {
        partsBox.replaceChildren();
        return;
      }
      partsBox.replaceChildren(
        h(
          'p',
          { class: 'settings-note' },
          'Parts are numbered largest first. Choose which part of the previous shape each part grows from.',
        ),
        ...to.parts.map((_, i) => {
          const select = h(
            'select',
            { 'aria-label': `Part ${i + 1} grows from` },
            ...Array.from({ length: Math.max(from.parts.length, to.parts.length) }, (_, j) =>
              h(
                'option',
                { value: j, selected: parts[i] === j },
                j < from.parts.length ? `Part ${j + 1}` : `A point (new part ${j + 1})`,
              ),
            ),
          );
          select.addEventListener('change', () => {
            parts = parts.map((p, k) => (k === i ? Number(select.value) : p));
            render();
          });
          return h('label', { class: 'settings-row' }, `Part ${i + 1} grows from `, select);
        }),
      );
    };

    /** Redraws everything from the current pins and parts. */
    const render = (): void => {
      drawShape(leftPane, fromD, from.anchors, 0, 'Previous shape');
      drawShape(rightPane, current.d, to.anchors, 1, 'This shape');
      drawParts();
      drawPins();
      drawPreview();
    };

    scrub.addEventListener('input', () => {
      progress = Number(scrub.value) / 100;
      drawPreview();
    });

    const auto = h('button', { type: 'button', title: 'Remove every pin and part choice' }, 'Auto');
    auto.addEventListener('click', () => {
      pins = [];
      parts = to.parts.map((_, i) => i);
      pending = null;
      render();
    });
    const cancel = h('button', { type: 'button' }, 'Cancel');
    cancel.addEventListener('click', () => {
      dialog.close();
    });
    const done = h('button', { type: 'button', class: 'primary' }, 'Done');
    done.addEventListener('click', () => {
      const next = draftValue();
      // Parts left in their automatic order are not stored, so the shape keeps matching itself.
      if (parts.every((p, i) => p === i) && !current.parts) delete next.parts;
      setKeyframeValue(store, keyframeId, next);
      dialog.close();
    });
    dialog.addEventListener('close', () => {
      dialog.remove();
      resolve();
    });

    dialog.append(
      h('header', { class: 'dialog-header' }, h('h3', null, 'Edit morph')),
      h(
        'div',
        { class: 'scene-settings' },
        h('div', { class: 'morph-panes' }, leftPane, rightPane, previewPane),
        message,
        pinList,
        partsBox,
        h('div', { class: 'dialog-actions' }, auto, cancel, done),
      ),
    );
    render();
    document.body.append(dialog);
    dialog.showModal();
  });
}
